import { authManager } from "@/lib/managers/auth-manager";
import { BUGSNAG_META_TAB, buildUserData } from "@/lib/utils/bugsnag-content-helper";
import {
   type CustomField,
   type JobTitle,
   type StreamResponseConversion,
   type TagOption,
} from "@/react/prop-types";
import { ActivityStore } from "@/stores/activity-store.core";
import { CompanyStore } from "@/stores/company-store.core";
import { CustomFieldStore } from "@/stores/custom-field-store.core";
import { EquipmentRegistryStore } from "@/stores/equipment-registry-store.core";
import { GroupStore } from "@/stores/group-store.core";
import { NotificationProfileStore } from "@/stores/notification-profile-store.core";
import { PermissionStore } from "@/stores/permission-store.core";
import { PersonStore } from "@/stores/person-store.core";
import { PositionStore } from "@/stores/position-store.core";
import { ProjectStore } from "@/stores/project-store.core";
import { SavedViewStore } from "@/stores/saved-view-store.core";
import { TagStore } from "@/stores/tag-store.core";
import { TimeOffStore } from "@/stores/time-off-store.core";
import type { NotifiableError } from "@bugsnag/js";
import Bugsnag from "@bugsnag/js";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import type { useI18nContext } from "@procore/core-react";
import { getProjectLabel } from "@/react/shared/helper";
import type { ActivityCategory } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import {
   FindTimeOffFilter,
   FindTimeOffSortBy,
} from "@laborchart-modules/common/dist/reql-builder/procedures/enums/find-time-off";
import type { ActivityEntityType } from "@laborchart-modules/common/dist/reql-builder/procedures/find-activity";
import { Order } from "@laborchart-modules/common/dist/reql-builder/query-definitions";
import type { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import { PersonStatus } from "@laborchart-modules/common/dist/rethink/schemas/enums/people";
import { ProjectStatus } from "@laborchart-modules/common/dist/rethink/schemas/enums/projects";
import type { ProjectStatus as PostgresProjectStatus } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import type { Filter } from "@laborchart-modules/common/dist/rethink/schemas/generated-reports/common";
import {
   FilterClassifier,
   FilterFieldType,
} from "@laborchart-modules/common/dist/rethink/schemas/generated-reports/enums/common";
import type {
   FindTimeOffStreamQueryParams,
   SerializedFindTimeOff,
} from "@laborchart-modules/lc-core-api/dist/api/time-off/find-time-off";
import type { ValidatePhoneNumberResponse } from "@laborchart-modules/lc-core-api/dist/api/twilio/validate-phone-number";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { FIVE_MINUTES, TEN_MINUTES } from "@/react/components/common/constants/query-cache";
import { TwilioStoreCore } from "../../../../stores/twilio-store.core";
import { filterNameMaps } from "../../time-off-list/time-off-list-helpers";
import type { SerializedCachedEquipmentCompanyType } from "@laborchart-modules/common/dist/postgres/serializers/cached-equipment-company-type-serializer";
import type { GetEquipmentByTypeIdResponse } from "@laborchart-modules/lc-core-api/dist/api/equipment/get-equipment";

export function useGetTagOptionsQuery() {
   return useQuery({
      queryKey: ["tag-options"],
      queryFn: () => streamTagOptions(),
   });
}

async function getCustomFields(entityType: CustomFieldEntity | CustomFieldEntity[]) {
   const entityTypesArray = Array.isArray(entityType) ? entityType : [entityType];
   const customFields: CustomField[] = [];
   const stream = await CustomFieldStore.findCustomFieldsStream({
      is_on_entities: entityTypesArray,
   }).stream;
   for await (const field of stream) {
      customFields.push(field);
   }
   return customFields;
}

export function useGetCustomFields(entityType: CustomFieldEntity | CustomFieldEntity[]) {
   return useQuery({
      queryKey: ["custom-fields", entityType],
      queryFn: () => getCustomFields(entityType),
   });
}

async function getIntegratedFields() {
   return await CompanyStore.getIntegratedFields().payload;
}

export function useGetIntegratedFields() {
   return useQuery({
      queryKey: ["integrated-fields"],
      queryFn: () => getIntegratedFields(),
   });
}

async function streamPeople(group_id?: string) {
   const params = { group_id, status: PersonStatus.ACTIVE };
   const stream = await PersonStore.findPeopleStream(params).stream;
   const data = [];
   for await (const item of stream) {
      data.push({
         id: item.id,
         label: `${item.name.first} ${item.name.last}`,
         can_recieve_email: item.can_receive_email,
         can_recieve_sms: item.can_receive_sms,
         can_recieve_mobile: item.can_receive_mobile,
         first_name: item.name.first,
         last_name: item.name.last,
      });
   }
   return data;
}

export function useGetPeopleStream(group_id?: string) {
   return useQuery({
      queryKey: ["people-stream", { group_id }],
      queryFn: () => streamPeople(group_id),
   });
}

async function getSavedView(viewId: string) {
   return await SavedViewStore.getSavedView(viewId).payload;
}

export function useGetSavedView(viewId: string | null) {
   return useQuery({
      queryKey: ["saved-view", { viewId }],
      // Null guard works here because we are using the enabled flag to disable the query if viewId is null
      queryFn: () => getSavedView(viewId!),
      enabled: !!viewId,
   });
}

async function getCompany() {
   return await CompanyStore.getCompany().payload;
}

export function useGetCompany() {
   return useQuery({
      queryKey: ["company"],
      queryFn: () => getCompany(),
   });
}

async function getJobTitles(groupId?: string) {
   const params = groupId !== "my-groups" ? { group_id: groupId } : {};
   const jobTitles: JobTitle[] = [];
   const stream = await PositionStore.findPositionsStream(params).stream;
   for await (const jobTitle of stream) {
      if (jobTitle.id && jobTitle.name) {
         jobTitles.push({
            id: jobTitle.id,
            label: jobTitle.name,
         });
      } else {
         console.error("skipping jobTitle due to null values:", jobTitle);
         Bugsnag.notify(`skipping jobTitle due to null values` as NotifiableError, (event) => {
            const user = authManager.authedUser();
            if (user) {
               event.addMetadata(
                  BUGSNAG_META_TAB.USER_DATA,
                  buildUserData(user, authManager.activePermission),
               );
            }
            event.severity = "error";
            event.context = "useGetJobTitles";
            event.addMetadata("jobTitle", { value: jobTitle, issue: "null values" });
         });
      }
   }
   return jobTitles;
}

export function useGetJobTitles(groupId?: string) {
   return useQuery({
      queryKey: ["job-titles", { groupId }],
      queryFn: () => getJobTitles(groupId),
   });
}

export const getEquipmentByTypeId = async (
   typeId: string,
): Promise<GetEquipmentByTypeIdResponse> => {
   return await EquipmentRegistryStore.getEquipmentByTypeId(typeId).payload;
};

const getEquipmentTypes = async () => {
   const equipmentTypes: SerializedCachedEquipmentCompanyType[] = [];
   const stream = await EquipmentRegistryStore.findEquipmentTypesStream().stream;
   for await (const equipmentType of stream) {
      equipmentTypes.push(equipmentType);
   }
   return equipmentTypes;
};

const thirtyMinutes = 30 * 60 * 1000;
export const useGetEquipmentTypes = () =>
   useQuery({
      queryKey: ["equipment-types"],
      queryFn: getEquipmentTypes,
      refetchOnWindowFocus: false,
      staleTime: thirtyMinutes,
      gcTime: thirtyMinutes,
   });

export async function streamPermissionLevels() {
   const permissions: Array<{ id: string; label: string; is_admin: boolean }> = [];
   const stream = await PermissionStore.findPermissionLevelsStream({}).stream;
   for await (const permissionLevel of stream) {
      permissions.push({
         id: permissionLevel.id,
         label: permissionLevel.name,
         is_admin: permissionLevel.is_admin,
      });
   }
   return permissions;
}

export function useGetPermissionLevelOptions() {
   return useQuery({
      queryKey: ["permission-levels"],
      queryFn: () => streamPermissionLevels(),
   });
}

export async function streamPeopleOptions(
   groupId: string,
): Promise<Array<{ id: string; label: string }>> {
   const params = {
      group_id: groupId !== "my-groups" ? groupId : undefined,
      status: PersonStatus.ACTIVE,
      is_user: true,
   };

   const stream = await PersonStore.findPeopleStream(params).stream;

   const data = [];

   for await (const item of stream) {
      data.push({
         id: item.id,
         label: `${item.name.first} ${item.name.last}`,
      });
   }

   return data;
}

export function useStreamPeopleOptions(groupId: string) {
   return useQuery({
      queryKey: ["people-options", { groupId }],
      queryFn: () => streamPeopleOptions(groupId),
      enabled: groupId !== null,
      staleTime: FIVE_MINUTES,
      gcTime: TEN_MINUTES,
      refetchOnWindowFocus: false, // Disable refetch on window focus
   });
}

export function useGetGroupOptionsQuery() {
   const { data, isLoading, error } = useQuery({
      queryKey: ["group-options"],
      queryFn: async () => {
         const groups = [];
         const stream = await GroupStore.findGroupsStream().stream;
         for await (const { id, name } of stream) {
            groups.push({ id, label: name });
         }
         return groups;
      },
      // Add these options to control refetching behavior
      staleTime: FIVE_MINUTES,
      gcTime: TEN_MINUTES,
      refetchOnWindowFocus: false, // Disable refetch on window focus
   });
   return { data, isLoading, error };
}

export async function streamTagOptions(): Promise<TagOption[]> {
   const tags = [];
   const stream = await TagStore.findTagsStream({}).stream;
   if (stream) {
      for await (const {
         id,
         abbreviation,
         name,
         color,
         categories,
         group_ids,
         globally_accessible,
         require_expr_date,
      } of stream) {
         tags.push({
            id,
            label: abbreviation,
            name,
            color,
            categories,
            group_ids,
            globally_accessible,
            require_expr_date,
         });
      }
   }
   return tags;
}

export async function validatePhoneNumber(number: string): Promise<ValidatePhoneNumberResponse> {
   return await TwilioStoreCore.validateNumber(number).payload;
}

type FindActivityPaginatedParams = {
   entityId?: string;
   entityType?: string;
   includedCategories?: string[];
   limit?: number;
   groupIds?: string[];
   hideMyActivity?: boolean;
   // Defaults to requiring an entity, as the tearsheets do; the company feed has none.
   enabled?: boolean;
};

async function getActivities(
   {
      entityId,
      entityType,
      includedCategories,
      limit = 25,
      groupIds,
      hideMyActivity,
   }: FindActivityPaginatedParams,
   starting_after?: string,
) {
   const data = await ActivityStore.findActivityPaginated(
      {
         ...(entityId && { entity_id: entityId }),
         entity_type: entityType as ActivityEntityType,
         included_categories: includedCategories as ActivityCategory[],
         limit: limit,
         starting_after: starting_after,
         ...(groupIds && { group_ids: groupIds }),
         ...(hideMyActivity && { hide_my_activity: true }),
      },
      {},
   ).payload;
   return data;
}

export function useFindActivityPaginatedQuery({
   entityId,
   entityType,
   includedCategories,
   limit = 25,
   groupIds,
   hideMyActivity,
   enabled = !!entityId && !!entityType,
}: FindActivityPaginatedParams) {
   const params = { entityId, entityType, includedCategories, limit, groupIds, hideMyActivity };
   return useInfiniteQuery({
      queryKey: ["activities", params],
      // Page param is the starting_after value
      queryFn: ({ pageParam: starting_after }) => getActivities(params, starting_after),
      enabled,
      refetchOnMount: "always",
      getNextPageParam: (lastPage) => lastPage.pagination?.next_starting_after,
      initialPageParam: undefined as string | undefined,
      staleTime: FIVE_MINUTES,
   });
}

async function getUpcomingTimeOff(personId: string, pageType?: string) {
   const data = await fetchUpcomingTimeOff(personId, pageType);
   return data;
}

export function useGetUpcomingTimeOff(personId: string, pageType?: string) {
   return useQuery({
      queryKey: ["upcoming-time-off", { personId, pageType }],
      queryFn: () => getUpcomingTimeOff(personId, pageType),
      enabled: !!personId,
   });
}

export async function fetchUpcomingTimeOff(
   personId: string,
   pageType?: string,
): Promise<SerializedFindTimeOff[]> {
   const filters = [
      {
         name: FindTimeOffFilter.PERSON_ID,
         type: FilterFieldType.TEXT,
         property: FindTimeOffFilter.PERSON_ID,
         value_sets: [{ negation: false, value: personId }],
      },
   ] as Filter[];
   if (!pageType) {
      filters.push({
         name: filterNameMaps.start_date,
         type: FilterFieldType.DATE,
         property: FindTimeOffFilter.START_DATE,
         value_sets: [
            {
               negation: false,
               value: getDetachedDay(new Date()),
               classifier: FilterClassifier.GREATER_THAN_OR_EQUAL,
            },
         ],
      });
   }

   const query: FindTimeOffStreamQueryParams = {
      filters: filters,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      sort_by: FindTimeOffSortBy.START_DATE,
      sort_direction: Order.ASCENDING,
   };
   const data = await TimeOffStore.findTimeOffStream(query).stream;

   const timeOffOptions: SerializedFindTimeOff[] = [];

   for await (const timeOff of data) {
      timeOffOptions.push(timeOff);
   }

   return timeOffOptions;
}

async function getNotificationProfiles(): Promise<StreamResponseConversion> {
   const stream = await NotificationProfileStore.findNotificationProfilesStream({}).stream;
   const notificationProfiles = [];
   for await (const notificationProfile of stream) {
      notificationProfiles.push({
         id: notificationProfile.id,
         label: notificationProfile.name,
      });
   }
   return notificationProfiles;
}

export function useGetNotificationProfiles() {
   return useQuery({
      queryKey: ["notification-profiles"],
      queryFn: () => getNotificationProfiles(),
   });
}

async function getProjectStages() {
   const response = await ProjectStore.getProjectStages().payload;
   return response.data.map((stage: any) => ({
      id: stage.cornerstone_stage_id,
      label: stage.cornerstone_stage_name,
   }));
}

export const useGetProjectStages = () => {
   const { data, isLoading, error } = useQuery({
      queryKey: ["project-stages"],
      queryFn: () => getProjectStages(),
   });

   return { data, isLoading, error };
};

export type ProjectPickerOption = {
   id: string;
   label: string;
   status: PostgresProjectStatus;
   stageId: string | null;
   stageName: string | null;
   /**
    * Group membership, so callers can narrow the list without a refetch.
    * Optional so existing option literals stay valid; the stream always
    * populates it.
    */
   groupIds?: string[];
};

async function getProjectsFilterOptions(
   groupId: string,
   includeAllStatuses = false,
): Promise<ProjectPickerOption[]> {
   const projects: ProjectPickerOption[] = [];
   const query = {
      ...(!includeAllStatuses && { status: ProjectStatus.ACTIVE }),
      ...(groupId !== "my-groups" && { group_id: groupId }),
   };
   const stream = await ProjectStore.findProjectsStream(query).stream;
   for await (const {
      id,
      name,
      job_number,
      status,
      cornerstone_stage_id,
      cornerstone_stage_name,
      group_ids,
   } of stream) {
      projects.push({
         id,
         label: job_number ? `${name} (${job_number})` : name,
         status,
         stageId: cornerstone_stage_id,
         stageName: cornerstone_stage_name,
         groupIds: group_ids ?? [],
      });
   }
   return projects;
}

// Passing I18n appends each project's job number to its label
export async function streamProjects(groupId?: string, I18n?: ReturnType<typeof useI18nContext>) {
   const params = groupId && groupId !== "my-groups" ? { group_id: groupId } : {};
   const stream = await ProjectStore.findProjectsStream(params).stream;
   const projects = [];
   for await (const project of stream) {
      projects.push({
         id: project.id,
         label: I18n ? getProjectLabel(project.name, project.job_number, I18n) : project.name,
      });
   }
   return projects;
}

export function useGetProjects(groupId?: string, I18n?: ReturnType<typeof useI18nContext>) {
   return useQuery({
      queryKey: ["projects-stream", { groupId, withJobNumber: I18n != null }],
      queryFn: () => streamProjects(groupId ?? "my-groups", I18n),
   });
}

export function useGetProjectFilterOptionsQuery(groupId: string, includeAllStatuses = false) {
   return useQuery({
      queryKey: ["project-filter-options", { groupId, includeAllStatuses }],
      queryFn: () => getProjectsFilterOptions(groupId, includeAllStatuses),
   });
}

async function getProjectCustomFieldFilters(groupId: string) {
   const params = new URLSearchParams({
      "entities[0]": "projects_custom_field_filters",
      group_id: groupId,
   });
   const response = await CompanyStore.getCompanyGroupOptions(params.toString()).payload;
   return response?.data?.projects_custom_field_filters ?? [];
}

export function useGetProjectCustomFieldFilters(groupId: string) {
   return useQuery({
      queryKey: ["project-custom-field-filters", { groupId }],
      queryFn: () => getProjectCustomFieldFilters(groupId),
   });
}

async function getPeopleCustomFieldFilters(groupId: string) {
   const params = new URLSearchParams({
      "entities[0]": "people_custom_field_filters",
      group_id: groupId,
   });
   const response = await CompanyStore.getCompanyGroupOptions(params.toString()).payload;
   return response?.data?.people_custom_field_filters ?? [];
}

export function useGetPeopleCustomFieldFilters(groupId: string) {
   return useQuery({
      queryKey: ["people-custom-field-filters", { groupId }],
      queryFn: () => getPeopleCustomFieldFilters(groupId),
   });
}
